import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { QueryClientProvider } from '@tanstack/react-query'
import jaLocale from 'date-fns/locale/ja'
import { NextPage } from 'next'
import App, { AppContext } from 'next/app'
import { SnackbarProvider } from 'notistack'
import { ReactElement, ReactNode, createContext, useEffect } from 'react'
import { io } from 'socket.io-client'

import type { AppProps } from 'next/app'

import { UseTfaToken, useTfaToken } from '@src/hooks/use-tfa-token'
import { useTrackEvent } from '@src/hooks/use-track-event/use-track-event'
import {
  queryClient,
  UpsiderDefaultTheme,
  PresidentCardDefaultTheme,
  initializeFirebaseApp,
} from '@src/libs'
import { isPresident } from '@src/utils'
import { PRESIDENT_CARD_BRAND_NAME } from '@src/utils/brand/const'

// start disableReactDevTools
// I thought about putting it in utils, but since it is only used in _app.tsx, I will temporarily write it here.
// We will move it when we have a good place to put it.
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __REACT_DEVTOOLS_GLOBAL_HOOK__: any
  }
}

function isFunction(obj: unknown) {
  return typeof obj == 'function'
}

function isObject(obj: unknown) {
  const type = typeof obj
  return type === 'function' || (type === 'object' && !!obj)
}

function hasWindowObject() {
  return typeof window !== 'undefined' && window.document
}

function disableReactDevTools() {
  if (hasWindowObject()) {
    // Ensure the React Developer Tools global hook exists
    if (!isObject(window.__REACT_DEVTOOLS_GLOBAL_HOOK__)) {
      return
    }

    // Replace all global hook properties with a no-op function or a null value
    for (const prop in window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      if (prop === 'renderers') {
        // prevents console error when dev tools try to iterate of renderers
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = new Map()
        continue
      }
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = isFunction(
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop]
      )
        ? Function.prototype
        : null
    }
  }
}
// end disableReactDevTools

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

if (process.env.NODE_ENV === 'production') disableReactDevTools()

initializeFirebaseApp()
if (process.env.NEXT_PUBLIC_BRAND === PRESIDENT_CARD_BRAND_NAME) {
  import('@src/styles/president/global.scss')
} else {
  import('@src/styles/global.scss')
}

export const TfaTokenContext = createContext<UseTfaToken | null>(null)
function MyApp({ Component, pageProps, ...props }: AppPropsWithLayout) {
  const { initializeTracking } = useTrackEvent()
  // Start hot reload
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const socket = io('http://0.0.0.0:8100')
      socket.on('reload', () => {
        location.reload()
      })
      return () => {
        socket.disconnect()
      }
    }
  }, [])
  // End hot reload

  useEffect(() => {
    initializeTracking()
  }, [])

  const getLayout = Component.getLayout ?? ((page) => page)
  return (
    <TfaTokenContext.Provider value={useTfaToken()}>
      <SnackbarProvider hideIconVariant>
        <QueryClientProvider client={queryClient}>
          <AppCacheProvider
            {...{
              ...Component,
              ...pageProps,
              ...props,
            }}
          >
            <ThemeProvider
              theme={
                isPresident() ? PresidentCardDefaultTheme : UpsiderDefaultTheme
              }
            >
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={jaLocale}
                dateFormats={{ monthAndYear: 'yyyy年MM月' }}
              >
                <CssBaseline />
                {getLayout(<Component {...pageProps} />)}
              </LocalizationProvider>
            </ThemeProvider>
          </AppCacheProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </TfaTokenContext.Provider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  // Use getInitialProps to use kubernetes environment variables.
  // https://nextjs.org/docs/api-reference/next.config.js/runtime-configuration
  // https://goraft.tech/2022/06/27/NextJS-in-Docker.html
  const appProps = await App.getInitialProps(appContext)
  return { ...appProps }
}

export default MyApp
